<template>
	<BaseModal
		v-model:visible="ss.questionModalVisible"
		@hide="close"
		@cancel="cancel"
		@ok="ok"
		:header="current.header"
		:show-cancel-button="true"
		:show-ok-button="true"
		:cancel-label="current.cancel"
		:ok-label="current.ok"
		:use-gradient-background="true"
	>
		<template #content>
			<div class="flex items-center gap-4">
				<GlyphIcon :icon="GlyphIconName['circle-empty-question']" size="64" color="var(--p-primary-500)" />
				<div class="message">{{ current.message }}</div>
			</div>
		</template>
	</BaseModal>
</template>

<script setup lang="ts">
import { useSettingsStore } from '@/stores/Settings'
import { ref } from 'vue'
import type { QuestionModalMessage, QuestionModalMessageQueue } from '../../definitions/Common'
import globalEvents from '@/utilities/GlobalEvents'
import { GlyphIconName } from '@/stores/GlyphIcons.types'
import { useI18n } from 'vue-i18n'
import BaseModal from './BaseModal.vue'

const ss = useSettingsStore()
const { t } = useI18n()
const queue = ref<QuestionModalMessageQueue>([])
const current = ref<QuestionModalMessage>({ header: '', message: '' })

async function show(data: QuestionModalMessage) {
	current.value = data
	current.value.ok = data.ok || t('common.ok')
	current.value.cancel = data.cancel || t('common.cancel')
	ss.questionModalVisible = true
	ss.questionModalCurrent = data
}

function queueOrShow(data: QuestionModalMessage) {
	if (!data) { return }
	if (!ss.questionModalVisible) {
		show(data)
	} else {
		if (current.value && current.value.header === data.header && current.value.message === data.message) { return } // Same message as currently showing - drop it
		if (queue.value.find(queued => { return queued.header === data.header && queued.message === data.message })) { return } // Same message is in queue - drop it
		queue.value.push(data)
	}
}

function close() {
	if (queue.value.length > 0) {
		setTimeout(() => {
			current.value = queue.value.shift() as QuestionModalMessage
			ss.questionModalCurrent = current.value
			ss.questionModalVisible = true
		}, 500)
	} else {
		ss.questionModalVisible = false
		ss.questionModalCurrent = undefined
	}
}

function cancel() {
	ss.questionModalVisible = false
	if (current.value?.cancelledEmit) { globalEvents.emit(current.value.cancelledEmit, current.value.cancelledEmitData as string) }
	ss.questionModalCurrent = undefined
}

function ok() {
	ss.questionModalVisible = false
	if (current.value?.emit) { globalEvents.emit(current.value.emit, current.value.emitData as string) }
	ss.questionModalCurrent = undefined
}

globalEvents.on('CommonQuestionModal', queueOrShow)

globalEvents.on('CommonQuestionModalDismiss', () => {
	if (ss.questionModalVisible) { close() }
})

globalEvents.on('Wakeup', () => {
	queue.value = []
	ss.questionModalVisible = false
	ss.questionModalCurrent = undefined
})

</script>

<style scoped>
.message {
	color: var(--p-primary-800);
}
@media (prefers-color-scheme: dark) {
	.message {
		color: var(--p-primary-300);
	}
}
</style>
